import axios from "axios";

const BASE_URL = (
  process.env.REACT_APP_BASE_URL ||
  "https://services.allhomedesire.com"
).replace(/\/+$/, "");

export const getAuthHeaders = (token) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

/* =========================================================================
   1. CUSTOMER LEADS APIS
   ========================================================================= */

// List customer's created leads
export const getCustomerLeads = async (token, { page_number = 1, page_size = 20, order_by = "DESC" } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/customer-lead/leads`,
    { page_number, page_size, order_by },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

// Get lead purposes dropdown options
export const getCustomerLeadPurposes = async (token) => {
  const response = await axios.post(
    `${BASE_URL}/customer-lead-purpose/all`,
    { page_number: 1, page_size: 50, order_by: "ASC" },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

// Add new customer lead
export const addCustomerLead = async (token, leadData) => {
  const response = await axios.post(
    `${BASE_URL}/customer-lead/add`,
    leadData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

// Edit existing customer lead
export const editCustomerLead = async (token, leadData) => {
  const response = await axios.put(
    `${BASE_URL}/customer-lead/edit`,
    leadData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

// Delete customer lead
export const deleteCustomerLead = async (token, leadId) => {
  const response = await axios.delete(
    `${BASE_URL}/customer-lead/delete/${leadId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

// Get lead by ID
export const getCustomerLeadById = async (token, leadId) => {
  const response = await axios.get(
    `${BASE_URL}/customer-lead/${leadId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

// Lead notes
export const getCustomerLeadNotes = async (token, leadId) => {
  const response = await axios.post(
    `${BASE_URL}/customer-lead/notes`,
    {
      page_number: 1,
      page_size: 100,
      order_by: "ASC",
      keyword_search: "",
      lead_id: leadId,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const addCustomerLeadNote = async (token, { leadId, note, parentId = 0 }) => {
  const response = await axios.post(
    `${BASE_URL}/customer-lead/note/add`,
    {
      customerLead: { id: leadId },
      note,
      parent_id: parentId,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const editCustomerLeadNote = async (token, noteObj) => {
  const response = await axios.put(
    `${BASE_URL}/customer-lead/note/edit`,
    noteObj,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const deleteCustomerLeadNote = async (token, noteId) => {
  const response = await axios.delete(
    `${BASE_URL}/customer-lead/note/delete/${noteId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   2. REWARDS & POINTS APIS
   ========================================================================= */

export const getRewardPointsCount = async (token) => {
  const response = await axios.get(`${BASE_URL}/customer/rewards/count`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

export const getRewardLogs = async (token) => {
  const response = await axios.get(`${BASE_URL}/customer/rewards/log`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

export const getRewardActions = async (token) => {
  const response = await axios.get(`${BASE_URL}/customer/rewards`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

export const getRedeemHistory = async (token) => {
  const response = await axios.get(`${BASE_URL}/customer/rewards/redeem/history`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

export const redeemRewardPoints = async (token, points) => {
  const response = await axios.post(
    `${BASE_URL}/customer/rewards/redeem`,
    { points: Number(points) },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   3. HEALTHIANS / SERVICE LEADS APIS
   ========================================================================= */

export const getHealthiansLeads = async (token, { page_number = 1, page_size = 20, order_by = "DESC", keyword_search = "" } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/admin/service/leads`,
    { page_number, page_size, order_by, keyword_search },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const addHealthiansLead = async (token, leadData) => {
  const response = await axios.post(
    `${BASE_URL}/admin/service/lead/add`,
    leadData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const editHealthiansLead = async (token, leadData) => {
  const response = await axios.put(
    `${BASE_URL}/admin/service/lead/edit`,
    leadData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const deleteHealthiansLead = async (token, leadId) => {
  const response = await axios.delete(
    `${BASE_URL}/admin/service/lead/delete/${leadId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getHealthiansLeadNotes = async (token, leadId) => {
  const response = await axios.post(
    `${BASE_URL}/admin/service/notes`,
    {
      page_number: 1,
      page_size: 50,
      order_by: "ASC",
      keyword_search: "",
      lead_id: leadId,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const addHealthiansLeadNote = async (token, noteData) => {
  const response = await axios.post(
    `${BASE_URL}/admin/service/note/add`,
    noteData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const deleteHealthiansLeadNote = async (token, noteId) => {
  const response = await axios.delete(
    `${BASE_URL}/admin/service/note/delete/${noteId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   4. MEETING REMINDERS APIS
   ========================================================================= */

export const getMeetingReminders = async (
  token,
  {
    pageNumber = 1,
    pageSize = 20,
    orderBy = "DESC",
    referenceType = "property",
    referenceId = 0,
  } = {}
) => {
  const payload = {
    pageNumber,
    pageSize,
    orderBy,
    property_id: referenceType === "property" ? referenceId : 0,
    local_bazaar_id: referenceType === "local_bazaar" ? referenceId : 0,
    interiror_design_id: referenceType === "interiror_design" ? referenceId : 0,
    all_services_id: referenceType === "all_services" ? referenceId : 0,
  };

  const response = await axios.post(
    `${BASE_URL}/meeting/reminders/property/list`,
    payload,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const createMeetingReminder = async (token, reminderData) => {
  const response = await axios.post(
    `${BASE_URL}/meeting/reminders/create`,
    reminderData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const updateMeetingReminder = async (token, reminderData) => {
  const response = await axios.put(
    `${BASE_URL}/meeting/reminders/update`,
    reminderData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const deleteMeetingReminder = async (token, reminderId) => {
  const response = await axios.delete(
    `${BASE_URL}/meeting/reminders/delete/${reminderId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   5. NEIGHBORHOOD COMMUNITIES APIS
   ========================================================================= */

export const getPublicCommunities = async (token, { page_number = 1, page_size = 50, order_by = "DESC" } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/customer/getAllPublicCommunities`,
    { page_number, page_size, order_by },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getMemberCommunities = async (token, { page_number = 1, page_size = 50, order_by = "DESC" } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/customer/getUserMemberCommunities`,
    { page_number, page_size, order_by },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getAdminCommunities = async (token, { page_number = 1, page_size = 50, order_by = "DESC" } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/customer/getUserAdminCommunities`,
    { page_number, page_size, order_by },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const addCommunity = async (token, communityData) => {
  const response = await axios.post(
    `${BASE_URL}/customer/addCommunity`,
    communityData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const joinCommunity = async (token, communityId) => {
  const response = await axios.post(
    `${BASE_URL}/customer/joinCommunity/${communityId}`,
    {},
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const leaveCommunity = async (token, communityId) => {
  const response = await axios.post(
    `${BASE_URL}/customer/leaveCommunity/${communityId}`,
    {},
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const deleteCommunity = async (token, communityId) => {
  const response = await axios.delete(
    `${BASE_URL}/customer/deleteCommunity/${communityId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getCommunityDetails = async (token, communityId) => {
  const response = await axios.post(
    `${BASE_URL}/customer/viewCommunityDetails/${communityId}`,
    {},
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getCommunityMembers = async (token, communityId, { page_number = 1, page_size = 50 } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/customer/getAllCommunityMembers/${communityId}`,
    { page_number, page_size, order_by: "ASC" },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const removeUserFromCommunity = async (token, communityId, targetUserId) => {
  const response = await axios.post(
    `${BASE_URL}/customer/removeUserFromCommunityAsAdmin/${communityId}/${targetUserId}`,
    {},
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   6. CONTACT & NETWORK INVITATIONS APIS
   ========================================================================= */

export const getInviteCounts = async (token) => {
  const [contactRes, networkRes, totalRes] = await Promise.all([
    axios.get(`${BASE_URL}/customer/contact/invite/count`, { headers: getAuthHeaders(token) }),
    axios.get(`${BASE_URL}/customer/network/invite/count`, { headers: getAuthHeaders(token) }),
    axios.get(`${BASE_URL}/customer/all/invite/count`, { headers: getAuthHeaders(token) }),
  ]);

  return {
    contactCount: contactRes.data?.inviteCount || 0,
    networkCount: networkRes.data?.inviteCount || 0,
    totalCount: totalRes.data?.inviteCount || 0,
  };
};

export const getReceivedContactInvites = async (token) => {
  const response = await axios.get(`${BASE_URL}/customer/contact/invites/receive`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

export const getReceivedNetworkInvites = async (token) => {
  const response = await axios.get(`${BASE_URL}/customer/network/invites/receive`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

export const getSentNetworkInvites = async (token) => {
  const response = await axios.get(`${BASE_URL}/customer/network/invites/send`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

export const acceptNetworkInvite = async (token, inviteId) => {
  const response = await axios.post(
    `${BASE_URL}/customer/network/invite/accept/${inviteId}`,
    {},
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const denyNetworkInvite = async (token, inviteId) => {
  const response = await axios.post(
    `${BASE_URL}/customer/network/invite/deny/${inviteId}`,
    {},
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   7. BROWSE HOME SERVICES & PROVIDERS APIS
   ========================================================================= */

export const getServiceCategories = async (token, keyword = "") => {
  const response = await axios.post(
    `${BASE_URL}/api/all/services/types`,
    {
      page_number: 1,
      page_size: 50,
      keyword_search: keyword,
      order_by: "DESC",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getServiceProvidersByCategory = async (
  token,
  {
    categoryId,
    searchText = "",
    filterState = "",
    filterCity = "",
    page = 1,
    pageSize = 50,
  }
) => {
  const payload = {
    all_service_category_id: categoryId,
    name: searchText,
    state: filterState,
    city: filterCity,
    order_by: "ASC",
    pageNumber: page,
    pageSize,
  };

  const response = await axios.post(
    `${BASE_URL}/admin/users/all-services-category`,
    payload,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getServicesByUser = async (token, userId, page = 1) => {
  const response = await axios.post(
    `${BASE_URL}/api/all/services/mySpace`,
    {
      user_id: userId,
      keywordSearch: "",
      typeId: "",
      page_number: page,
      page_size: 20,
      order_by: "DESC",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getServiceDetails = async (token, serviceId) => {
  const response = await axios.get(
    `${BASE_URL}/api/all/services/details/${serviceId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const toggleServiceFavorite = async (token, serviceId, isCurrentlyFavorite, favoriteId) => {
  if (isCurrentlyFavorite && favoriteId) {
    const response = await axios.delete(
      `${BASE_URL}/api/all/services/favorite/delete/${favoriteId}`,
      { headers: getAuthHeaders(token) }
    );
    return response.data;
  } else {
    const response = await axios.post(
      `${BASE_URL}/api/all/services/favorite/add`,
      { service_id: serviceId },
      { headers: getAuthHeaders(token) }
    );
    return response.data;
  }
};

/* =========================================================================
   8. BROWSE LOCAL BAZAAR MARKETPLACE APIS
   ========================================================================= */

export const getLocalBazaarCategories = async (token, keyword = "") => {
  const response = await axios.post(
    `${BASE_URL}/api/local/bazaar/categories/list`,
    {
      page_number: 1,
      page_size: 50,
      keyword_search: keyword,
      order_by: "DESC",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getLocalBazaarProducts = async (token, { page = 1, pageSize = 20, merchantId = null } = {}) => {
  const payload = {
    page_number: page,
    page_size: pageSize,
    order_by: "DESC",
  };
  if (merchantId) {
    payload.user_id = merchantId;
  }

  const response = await axios.post(
    `${BASE_URL}/api/local/bazaar/`,
    payload,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getLocalBazaarDetails = async (token, bazaarId) => {
  const response = await axios.get(
    `${BASE_URL}/api/local/bazaar/details/${bazaarId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const toggleLocalBazaarFavorite = async (token, bazaarId, isCurrentlyFavorite, favoriteId) => {
  if (isCurrentlyFavorite && favoriteId) {
    const response = await axios.delete(
      `${BASE_URL}/api/local/bazaar/favorite/delete/${favoriteId}`,
      { headers: getAuthHeaders(token) }
    );
    return response.data;
  } else {
    const response = await axios.post(
      `${BASE_URL}/api/local/bazaar/favorite/add`,
      { service_id: bazaarId },
      { headers: getAuthHeaders(token) }
    );
    return response.data;
  }
};

/* =========================================================================
   9. BRAND AMBASSADOR & SUBSCRIPTION PLANS APIS
   ========================================================================= */

export const becomeBrandAmbassador = async (token, user, locationData = {}) => {
  const payload = {
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    email_address: user.email_address || user.email,
    city: locationData.city || user.city || "",
    state: locationData.state || user.state || "",
    is_brand_ambassador: true,
  };

  const response = await axios.put(
    `${BASE_URL}/admin/editProfile`,
    payload,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getCustomerSubscriptionPlans = async (token, serviceId = 2) => {
  const response = await axios.post(
    `${BASE_URL}/api/subscription/plan/list/all/by/service`,
    {
      order_by: "ASC",
      all_service_id: Number(serviceId),
      pageNumber: 1,
      pageSize: 50,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};
